import type {
	AMLApproveRequest,
	AMLCheck,
	AMLCheckStatus,
	AMLMetadata,
	AMLRecheckRequest,
	AMLRejectRequest,
	AMLRiskLevel,
	AMLTransaction,
	AMLTransactionsPagination,
	ListAMLTransactionsParams,
} from "src/types";
import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

interface BackendAMLTransaction {
	id: number;
	hash: string | null;
	status: string;
	type: string;
	amount: string;
	currency: string;
	blockchain: string;
	sender_address: string;
	recipient_address: string;
	merchant_name: string | null;
	user_email: string | null;
	customer_email: string | null;
	order_uuid: string | null;
	order_merchant_id: string | null;
	created_at: string;
	updated_at: string;
	aml_check_id: number | null;
	risk_score: number | null;
	risk_level: string | null;
	aml_status: string | null;
	aml_provider: string | null;
	aml_checked_at: string | null;
	aml_error: string | null;
}

interface BackendListResponse {
	transactions: BackendAMLTransaction[];
	total: number;
	limit: number;
	offset: number;
}

interface BackendTransactionDetails {
	id: number;
	hash: string | null;
	status: string;
	type: string;
	amount: string;
	currency: string;
	blockchain: string;
	network_id: string;
	sender_address: string | null;
	recipient_address: string;
	service_fee: string;
	network_fee: string | null;
	usd_amount: string | null;
	merchant_id: number;
	merchant_name: string | null;
	user_email: string | null;
	customer_email: string | null;
	payment_id: number | null;
	payment_public_id: string | null;
	created_at: string;
	updated_at: string;
	metadata: Record<string, string>;
}

interface BackendAMLCheckDetails {
	id: number;
	transaction_id: number;
	wallet_id: number;
	sender_address: string;
	provider: string;
	risk_score: number;
	risk_level: string;
	status: string;
	checked_at: string;
	created_at: string;
	metadata?: AMLMetadata;
	error_message: string | null;
}

interface BackendTransactionAMLDetailsResponse {
	transaction: BackendTransactionDetails;
	aml_check: BackendAMLCheckDetails | null;
}

export interface TransactionAMLDetails {
	transaction: AMLTransaction;
	amlCheck: AMLCheck | null;
}

function transformTransaction(tx: BackendAMLTransaction): AMLTransaction {
	return {
		id: tx.id,
		hash: tx.hash,
		type: tx.type,
		status: tx.status as AMLTransaction["status"],
		amount: tx.amount,
		currency: tx.currency,
		blockchain: tx.blockchain,
		senderAddress: tx.sender_address || null,
		recipientAddress: tx.recipient_address,
		merchantName: tx.merchant_name,
		userEmail: tx.user_email,
		customerEmail: tx.customer_email,
		orderUuid: tx.order_uuid,
		orderMerchantId: tx.order_merchant_id,
		createdAt: tx.created_at,
		updatedAt: tx.updated_at,
		amlCheck: tx.aml_check_id
			? {
					id: String(tx.aml_check_id),
					transactionId: tx.id,
					provider: tx.aml_provider ?? "unknown",
					riskScore: tx.risk_score ?? 0,
					riskLevel: (tx.risk_level ?? "low") as AMLRiskLevel,
					status: (tx.aml_status ?? "pending") as AMLCheckStatus,
					metadata: undefined,
					errorMessage: tx.aml_error,
					createdAt: tx.aml_checked_at ?? tx.created_at,
				}
			: null,
	};
}

const amlProvider = {
	async listTransactions(
		params: ListAMLTransactionsParams = {},
	): Promise<AMLTransactionsPagination> {
		const response = await apiRequest.get<BackendListResponse>(
			withApiPath("/aml/transactions"),
			{
				params: {
					limit: params.limit ?? 50,
					offset: params.offset ?? 0,
					aml_status: params.amlStatus ?? "",
					risk_level: params.riskLevel ?? "",
					tx_status: params.txStatus ?? "",
				},
			},
		);

		return {
			transactions: response.data.transactions.map(transformTransaction),
			total: response.data.total,
		};
	},

	async getTransactionDetails(
		transactionId: number,
	): Promise<TransactionAMLDetails> {
		const response = await apiRequest.get<BackendTransactionAMLDetailsResponse>(
			withApiPath(`/aml/transactions/${transactionId}`),
		);

		const { transaction: tx, aml_check: check } = response.data;

		const amlCheck: AMLCheck | null = check
			? {
					id: String(check.id),
					transactionId: check.transaction_id,
					provider: check.provider,
					riskScore: check.risk_score,
					riskLevel: check.risk_level as AMLRiskLevel,
					status: check.status as AMLCheckStatus,
					metadata: check.metadata,
					errorMessage: check.error_message,
					createdAt: check.created_at,
				}
			: null;

		return {
			transaction: {
				id: tx.id,
				hash: tx.hash,
				type: tx.type,
				status: tx.status as AMLTransaction["status"],
				amount: tx.amount,
				currency: tx.currency,
				blockchain: tx.blockchain,
				senderAddress: tx.sender_address,
				recipientAddress: tx.recipient_address,
				merchantName: tx.merchant_name,
				userEmail: tx.user_email,
				customerEmail: tx.customer_email,
				orderUuid: null,
				orderMerchantId: null,
				createdAt: tx.created_at,
				updatedAt: tx.updated_at,
				amlCheck,
			},
			amlCheck,
		};
	},

	// Deprecated: Use listTransactions instead
	async listHighRiskTransactions(
		params: ListAMLTransactionsParams = {},
	): Promise<AMLTransactionsPagination> {
		return this.listTransactions(params);
	},

	async approveTransaction(
		transactionId: number,
		params: AMLApproveRequest,
	): Promise<AMLActionResponse> {
		const response = await apiRequest.post<AMLActionResponse>(
			withApiPath(`/aml/transactions/${transactionId}/approve`),
			{
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async rejectTransaction(
		transactionId: number,
		params: AMLRejectRequest,
	): Promise<AMLActionResponse> {
		const response = await apiRequest.post<AMLActionResponse>(
			withApiPath(`/aml/transactions/${transactionId}/reject`),
			{
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async recheckTransaction(
		transactionId: number,
		params: AMLRecheckRequest,
	): Promise<AMLActionResponse> {
		const response = await apiRequest.post<AMLActionResponse>(
			withApiPath(`/aml/transactions/${transactionId}/recheck`),
			params,
		);
		return response.data;
	},

	async skipTransaction(
		transactionId: number,
		params: AMLApproveRequest,
	): Promise<AMLActionResponse> {
		const response = await apiRequest.post<AMLActionResponse>(
			withApiPath(`/aml/transactions/${transactionId}/skip`),
			{
				reason: params.reason,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},
};

interface AMLActionResponse {
	success: boolean;
	message: string;
	transaction_id: number;
	new_status: string;
}

export default amlProvider;
